import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";

import Layout from "@/components/Layout";
import TestModeBanner from "@/components/TestModeBanner";
import { CartProvider, useCart } from "@/context/CartContext";
import { ProductProvider } from "@/context/ProductContext";
import CartDrawer from "@/components/CartDrawer";

import Home from "@/pages/Home";
import Contact from "@/pages/Contact";
import Product from "@/pages/Product";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import ThankYou from "@/pages/ThankYou";
import Delivery from "@/pages/Delivery";
import Returns from "@/pages/Returns";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Disclaimer from "@/pages/Disclaimer";

import { AdminAuthProvider } from "@/admin/AdminAuth";
import AdminLogin from "@/admin/AdminLogin";
import AdminOrders from "@/admin/AdminOrders";
import AdminOrderDetail from "@/admin/AdminOrderDetail";
import AdminProducts from "@/admin/AdminProducts";
import AdminMessages from "@/admin/AdminMessages";
import AdminSubscribers from "@/admin/AdminSubscribers";
import AdminSettings from "@/admin/AdminSettings";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function CloseDrawerOnRouteChange() {
  const { closeDrawer } = useCart();
  const { pathname } = useLocation();
  useEffect(() => { closeDrawer(); }, [pathname, closeDrawer]);
  return null;
}

/**
 * Storefront wrapper — header, footer, banner, cart drawer.
 * Used for all non-admin routes.
 */
function Storefront({ children }) {
  return (
    <ProductProvider>
      <CartProvider>
        <CloseDrawerOnRouteChange />
        <TestModeBanner />
        <Layout>{children}</Layout>
        <CartDrawer />
      </CartProvider>
    </ProductProvider>
  );
}

/**
 * Admin wrapper — no storefront chrome; AdminLayout handles its own shell.
 */
function AdminShell({ children }) {
  return <AdminAuthProvider>{children}</AdminAuthProvider>;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* Admin section — own provider, no storefront chrome */}
        <Route path="/admin/login" element={<AdminShell><AdminLogin /></AdminShell>} />
        <Route path="/admin" element={<AdminShell><AdminOrders /></AdminShell>} />
        <Route path="/admin/orders" element={<AdminShell><AdminOrders /></AdminShell>} />
        <Route path="/admin/orders/:order_number" element={<AdminShell><AdminOrderDetail /></AdminShell>} />
        <Route path="/admin/products" element={<AdminShell><AdminProducts /></AdminShell>} />
        <Route path="/admin/messages" element={<AdminShell><AdminMessages /></AdminShell>} />
        <Route path="/admin/subscribers" element={<AdminShell><AdminSubscribers /></AdminShell>} />
        <Route path="/admin/settings" element={<AdminShell><AdminSettings /></AdminShell>} />

        {/* Storefront */}
        <Route path="/" element={<Storefront><Home /></Storefront>} />
        <Route path="/product" element={<Storefront><Product /></Storefront>} />
        <Route path="/cart" element={<Storefront><Cart /></Storefront>} />
        <Route path="/checkout" element={<Storefront><Checkout /></Storefront>} />
        <Route path="/thank-you" element={<Storefront><ThankYou /></Storefront>} />
        <Route path="/contact" element={<Storefront><Contact /></Storefront>} />
        <Route path="/delivery" element={<Storefront><Delivery /></Storefront>} />
        <Route path="/returns" element={<Storefront><Returns /></Storefront>} />
        <Route path="/privacy" element={<Storefront><Privacy /></Storefront>} />
        <Route path="/terms" element={<Storefront><Terms /></Storefront>} />
        <Route path="/disclaimer" element={<Storefront><Disclaimer /></Storefront>} />
        <Route path="*" element={<Storefront><Home /></Storefront>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
